import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { AbcRadioGroupComponent } from '@barthauer/abc/ui-components';
import { DxPopoverModule } from 'devextreme-angular';
import { DxRadioGroupTypes } from 'devextreme-angular/ui/radio-group';
import { FlexLayoutModule } from 'ngx-flexible-layout';
import { AuditItemQuestion } from '../../classes';
import { DataService } from '../../services/data.service';
import { IddatLoaderComponent } from '../loader/loader.component';

export interface Labels {
  Question?: string;
  NoQuestion?: string;
}
export const defaultLabels: Labels = {
  Question: 'Fragen',
  NoQuestion: 'Keine Fragen vorhanden',
};

@Component({
    selector: 'iddat-multiple-choice-list',
    templateUrl: './multiple-choice-list.component.html',
    styleUrls: ['./multiple-choice-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        FlexLayoutModule,
        MatIconModule,
        MatExpansionModule,
        DxPopoverModule,
        AbcRadioGroupComponent,
        IddatLoaderComponent,
    ]
})
export class MultipleChoiceListComponent implements OnChanges {
  @Input() questions: AuditItemQuestion[] = [];

  @Input() questionIndex = 0;
  @Output() questionIndexChange = new EventEmitter<number>();

  @Input() preserveQuestionIndex = false;

  @Input() deactivateNavigation = false;
  @Input() enableHeaderContent = false;
  @Input() disableCollapse = true;

  @Input() loading = false;

  @Input() labels: Labels = defaultLabels;

  @Input() title: (question?: AuditItemQuestion) => string = () =>
    `${this.labels.Question} ${this.questionIndex + 1}/${this.questionListLength}`;

  @Output() changeSelectedQuestion = new EventEmitter<{ index: number; question: AuditItemQuestion }>();
  @Output() changeAnswer = new EventEmitter<{ answer: string; question: AuditItemQuestion }>();

  @ViewChild(MatExpansionPanel) panel: MatExpansionPanel;
  @ViewChild('radioGroup') radioGroup: AbcRadioGroupComponent;

  public options: string[];
  public response: string;

  public descriptionPopoverVisible: boolean = false;
  public questionDescription: string;

  public questionListEmpty = false;
  private questionListLength: number;

  public expanded = true;

  constructor(public dataService: DataService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (Array.isArray(changes['questions']?.currentValue)) {
      this.questionListEmpty = changes['questions']?.currentValue.length ? false : true;

      this.questionListLength = this.questionListEmpty ? (changes['questions']?.previousValue?.length ?? 0) : changes['questions']?.currentValue.length;

      if (!this.preserveQuestionIndex) {
        this.questionIndex = this.questionListEmpty ? -1 : 0;
        this.questionIndexChange.emit(this.questionIndex);
      }

      this.prepareQuestionOptions();
    }

    if (changes['labels'] && Object.keys(changes['labels']?.currentValue).length > 0) {
      this.labels = { ...Object.assign(defaultLabels, changes['labels'].currentValue) };
    }
  }

  public preventPanelToggle(): void {
    if (this.disableCollapse) this.expanded = true;
  }

  public togglePopover(): void {
    this.descriptionPopoverVisible = !this.descriptionPopoverVisible;
  }

  public stepBack(): void {
    this.loading = true;
    if (!(this.deactivateNavigation || this.questionListEmpty || this.questionIndex === 0)) {
      this.questionIndex--;
      this.questionIndexChange.emit(this.questionIndex);

      this.prepareQuestionOptions();
    }

    this.panel.toggle();
    this.loading = false;
  }

  public stepForward(): void {
    this.loading = true;
    if (!(this.deactivateNavigation || this.questionListEmpty || this.questionIndex === this.questions?.length - 1)) {
      this.questionIndex++;
      this.questionIndexChange.emit(this.questionIndex);

      this.prepareQuestionOptions();
    }

    this.panel.toggle();
    this.loading = false;
  }

  public prepareQuestionOptions(): void {
    const selectedQuestion = this.questions[this.questionIndex];
    this.changeSelectedQuestion.emit({ index: this.questionIndex, question: selectedQuestion });

    this.questionDescription = selectedQuestion?.QuestionDescription;

    this.options = [];
    this.response = null;
    this.radioGroup?.instance.clear();

    const selectedResponse =
      selectedQuestion?.SelectedResponse && selectedQuestion.SelectedResponse[0]?.Designation
        ? selectedQuestion.SelectedResponse[0]?.Designation
        : null;

    selectedQuestion?.Responses?.forEach((response) => {
      this.options.push(response.Designation);
      if (selectedResponse === response.Designation) {
        this.response = response.Designation;
      }
    });
  }

  public radioChange({ value, event }: DxRadioGroupTypes.ValueChangedEvent): void {
    if (event?.type === 'dxclick') {
      this.changeAnswer.emit({ answer: value, question: this.questions[this.questionIndex] });
    }
  }
}
